/**
 * BattleCode V2 — Movement System
 *
 * Phase 2: 从 engine-scheduler.ts applyMoveAction (L795-897) 抽出。
 * 7 档碰撞规则不变，改为接收 BattleState + ctx 操作。
 *
 * GDD §12.3 LOCK 7 档碰撞规则:
 *   1. tank ↔ tank         → 双方 tick 作废,停原位
 *   2. tank ↔ obstacle     → tank 停原位
 *   3. tank ↔ unreachable  → 视同 obstacle
 *   4. tank ↔ 边界         → tick 作废,停原位
 *   5. tank ↔ 非法坐标     → tick 作废(已在 bot-api.py 截断)
 *   死人不移动(规则 0:死人 noop)
 */

import type { BattleState, BotAction } from "../types";
import { chebyshevDistance } from "../tank-stats";
import { isObstacleAt } from "../map-utils";
import type { TickContext, CoverUseFlags } from "../modes/game-mode.interface";

export class MovementSystem {
  // 2026-07-12: headless 模式标记 + 连续碰撞退避
  // Arena headless 战斗不需要 [STEP]/[STEP_BLOCKED] 细粒度日志
  private static _headlessMode = false;
  // 连续 tank_collision 计数器（按 bot 方向跟踪，打破死锁）
  private static _collisionCount = 0;
  private static readonly COLLISION_BACKOFF_THRESHOLD = 5;

  /** Arena headless 模式：关闭移动日志 + 启用碰撞退避 */
  static setHeadlessMode(enabled: boolean): void {
    MovementSystem._headlessMode = enabled;
    // headless 进入/退出都重置计数器（Arena 每次新战斗不应继承上次碰撞计数）
    MovementSystem._collisionCount = 0;
  }

  /**
   * FR-018 ADR-018.3:把 move action 应用到对应 tank 的 position
   * 迁移自 engine-scheduler.ts applyMoveAction (L795-897)，行为完全一致。
   *
   * @returns 更新后的 CoverUseFlags（applyMove 可能触发 COVER_USE）
   */
  static applyMove(
    state: BattleState,
    team: "team_a" | "team_b",
    action: BotAction,
    ctx: TickContext,
    coverFlags: CoverUseFlags,
  ): CoverUseFlags {
    if (action.type !== "move") return coverFlags;

    const isA = team === "team_a";
    const tank = isA ? state.self : state.enemy;
    if (!tank.is_alive) return coverFlags; // 死人 noop

    const mw = state.map_size.width;
    const mh = state.map_size.height;
    const currentPos = tank.position;
    const tx = Math.round(action.target.x);
    const ty = Math.round(action.target.y);

    // ===== GDD §12.3 #4:边界撞停(不再 clip)=====
    const outOfBounds = tx < 0 || tx >= mw || ty < 0 || ty >= mh;

    // ===== GDD §12.3 #2/#3:obstacle / unreachable 撞停 =====
    const blockedByObstacle = isObstacleAt(tx, ty, state.map_id);
    if (!outOfBounds && !blockedByObstacle) {
      // ===== GDD §12.3 #1:tank ↔ tank 撞停（V3: 检查所有敌方坦克位置）=====
      const otherPositions: Array<{ x: number; y: number }> = [];
      if (state.team_a && state.team_b) {
        // V3: 多坦克模式 — 检查对方队伍所有存活坦克的位置
        const otherTeam = isA ? state.team_b.tanks : state.team_a.tanks;
        for (const ot of otherTeam) {
          if (ot.is_alive) otherPositions.push(ot.position);
        }
      } else {
        // 1v1 向后兼容
        otherPositions.push(isA ? state.enemy.position : state.self.position);
      }

      const collided = otherPositions.some((op) => tx === op.x && ty === op.y);
      if (collided) {
        ctx.emit({
          type: "key_event",
          eventType: "COLLISION",
          tickNum: ctx.tickNum,
          actor: isA ? "team_a" : "team_b",
          description: `${isA ? "玩家" : "AI"}撞停(坦克碰撞)`,
        });

        // 2026-07-12: 连续碰撞退避 — 5 次同方向碰撞后随机偏移，打破死锁
        MovementSystem._collisionCount++;
        if (MovementSystem._headlessMode && MovementSystem._collisionCount >= MovementSystem.COLLISION_BACKOFF_THRESHOLD) {
          MovementSystem._collisionCount = 0;
          const offsets = [[1, 0], [-1, 0], [0, 1], [0, -1]];
          for (let k = offsets.length - 1; k > 0; k--) {
            const ri = Math.floor(Math.random() * (k + 1));
            [offsets[k], offsets[ri]] = [offsets[ri], offsets[k]];
          }
          for (const [dx, dy] of offsets) {
            const bx = currentPos.x + dx;
            const by = currentPos.y + dy;
            if (bx < 0 || bx >= mw || by < 0 || by >= mh) continue;
            if (isObstacleAt(bx, by, state.map_id)) continue;
            if (otherPositions.some((op) => bx === op.x && by === op.y)) continue;
            const backoffPos = { x: bx, y: by };
            if (isA) state.self.position = backoffPos;
            else state.enemy.position = backoffPos;
            return coverFlags;
          }
        }

        if (!MovementSystem._headlessMode) {
          console.info(`[STEP_BLOCKED] tick=${ctx.tickNum} bot=${isA ? "player_a" : "ai_b"} at=(${currentPos.x},${currentPos.y}) reason=tank_collision`);
        }
        return coverFlags;
      }
    } else {
      const reason = outOfBounds ? "out_of_bounds" : "obstacle";
      const desc = outOfBounds ? "撞边界" : "撞障碍物";
      ctx.emit({
        type: "key_event",
        eventType: "COLLISION",
        tickNum: ctx.tickNum,
        actor: isA ? "team_a" : "team_b",
        description: `${isA ? "玩家" : "AI"}${desc}`,
      });
      if (!MovementSystem._headlessMode) {
        console.info(`[STEP_BLOCKED] tick=${ctx.tickNum} bot=${isA ? "player_a" : "ai_b"} at=(${currentPos.x},${currentPos.y}) reason=${reason} target=(${tx},${ty})`);
      }
      return coverFlags;
    }

    // 通过所有检查:更新 position
    const newPos = { x: tx, y: ty };
    if (isA) {
      state.self.position = newPos;
    } else {
      state.enemy.position = newPos;
    }

    // [STEP] 单格切比雪夫步细粒度日志（headless 模式下禁止输出）
    if (!MovementSystem._headlessMode) {
      const dx = newPos.x - currentPos.x;
      const dy = newPos.y - currentPos.y;
      let dir: string;
      if (dx === 0 && dy === 0) dir = "STAY";
      else if (dx === 0) dir = dy > 0 ? "DOWN" : "UP";
      else if (dy === 0) dir = dx > 0 ? "RIGHT" : "LEFT";
      else if (dx > 0 && dy > 0) dir = "DOWN-RIGHT";
      else if (dx > 0 && dy < 0) dir = "UP-RIGHT";
      else if (dx < 0 && dy > 0) dir = "DOWN-LEFT";
      else dir = "UP-LEFT";
      console.info(`[STEP] tick=${ctx.tickNum} bot=${isA ? "player_a" : "ai_b"} from=(${currentPos.x},${currentPos.y}) to=(${newPos.x},${newPos.y}) dir=${dir}`);
    }

    // P0-#9:COVER_USE 玩家/AI 第一次到达 cover 位置附近(切比雪夫距离 ≤ 1)
    if (state.nearest_cover) {
      const coverPos = state.nearest_cover.position;
      const distToCover = chebyshevDistance(newPos, coverPos);
      if (distToCover <= 1) {
        if (isA && !coverFlags.playerUsedCover) {
          coverFlags.playerUsedCover = true;
          ctx.emit({
            type: "key_event",
            eventType: "COVER_USE",
            tickNum: ctx.tickNum,
            actor: "team_a",
            description: "玩家躲到掩体",
          });
        } else if (!isA && !coverFlags.aiUsedCover) {
          coverFlags.aiUsedCover = true;
          ctx.emit({
            type: "key_event",
            eventType: "COVER_USE",
            tickNum: ctx.tickNum,
            actor: "team_b",
            description: "AI 躲到掩体",
          });
        }
      }
    }

    return coverFlags;
  }
}
